<template>
    <v-container class="rtls-text-field-container" :class="cssClass">
        <div class="rtls-text-field-container__label-wrapper">
            <p v-if="label" class="rtls-text-field-container__label">
                {{ toSentenceCase($t(label)) }}
            </p>
            <span v-if="isRequired" class="asterisk">*</span>
        </div>
        <div class="rtls-text-field-container__control">
            <v-text-field
                v-model="text"
                :data-id-e2e="dataIdE2E"
                :class="white ? 'rtls-text-field--white' : 'rtls-text-field--grey'"
                class="rtls-text-field"
                :placeholder="placeholder ? toSentenceCase($t(placeholder)) : ''"
                :disabled="isDisabled"
                :rules="rules"
                autocomplete="off"
                validate-on="blur"
                :hide-details="hideDetails"
                :clearable="clearable"
                @keydown.enter="handleEnter"
                @click:clear="clear()"
                @blur="onBlur"
                @input="handleInput"
                @change="change"
                @click="click"
            />
        </div>
    </v-container>
</template>

<script>
import { trim, debounce, isEmpty } from 'lodash';
import vuexComponentMixin from '../../mixins/vuex-component';
import readonly from '@/js/mixins/readonly';

export default {
    mixins: [vuexComponentMixin, readonly],

    props: {
        label: String,
        cssClass: String,
        clearable: {
            type: Boolean,
            default: false,
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        dataIdE2E: String,
        updateOnInput: {
            type: Boolean,
            default: false,
        },
        saveWhitespace: {
            type: Boolean,
            default: false,
        },
        preventDefault: Boolean,
        white: {
            type: Boolean,
            default: false,
            required: false,
        },
        hideDetails: {
            type: [Boolean, String],
            default: false,
        },
    },
    emits: ['input', 'blur', 'change', 'click', 'keydown', 'click:clear'],
    data() {
        return {
            textField: null,
        };
    },

    computed: {
        isDisabled() {
            return this.disabled || this.isReadOnly;
        },
        text: {
            get() {
                return this.model;
            },
            set(newValue) {
                this.textField = this.saveWhitespace ? newValue : trim(newValue);
            },
        },
    },

    created() {
        this.textField = this.model;
    },

    methods: {
        updateStagingArea() {
            this.model = this.textField;
        },

        onInput() {
            this.updateStagingArea();
            this.$emit('input');
        },

        debouncedOnInput: debounce(
            function() {
                this.onInput();
            },
            400,
            { leading: true }
        ),

        handleInput() {
            if (!this.updateOnInput) return;

            if (isEmpty(this.textField)) {
                this.onInput();
            } else {
                this.debouncedOnInput();
            }
        },

        onBlur() {
            this.textField = trim(this.textField);
            this.updateStagingArea();

            if (this.updateOnInput) return;

            this.$emit('blur');
        },

        change(value) {
            this.$emit('change', value);
        },

        click(event) {
            this.$emit('click', event);
        },
        handleEnter(event) {
            this.updateStagingArea();
            if (this.preventDefault) {
                event.preventDefault();
            } else {
                // nextTick is needed to allow model setter to be called and staging area to be updated.
                // without nextTick model can be outdated if the user presses enter button very quickly after text entering
                this.$nextTick(() => {
                    this.$emit('keydown', event);
                });
            }
        },
        clear() {
            this.$emit('click:clear');
        },
    },
};
</script>

<style lang="scss">
.rtls-text-field-container {
    .v-field--disabled {
        opacity: 0.9 !important;

        // Vuetify 3 renders the disabled bottom border as dashed; restore the solid line from Vue 2.
        .v-field__outline::before {
            border-image: none !important;
            border-color: rgba(0, 0, 0, 0.42) !important;
        }
    }

    .v-field__field input::placeholder {
        color: rgba(0, 0, 0, 1) !important;
    }
}
</style>
